import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CartService } from '../../../shared/services/cart.service';
import { PaynowService } from '../../../shared/services/paynow.service';
import { NewsletterService } from '../../../shared/services/newsletter.service';
import { getCheckoutDraftStorageKey } from '../../../shared/checkout-draft.util';

@Component({
  selector: 'app-checkout-success',
  standalone: false,
  template: `
    <div class="min-h-[70vh] bg-slate-50 flex items-center justify-center px-4 py-10 sm:py-16">
      <main class="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-9">
        <div class="flex flex-col items-center text-center">
          <div class="mb-5 flex h-14 w-14 items-center justify-center rounded-full"
            [ngClass]="getStatusIconClass()">
            <svg *ngIf="status === 'paid'" class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="m5 12 4 4L19 6"></path>
            </svg>
            <svg *ngIf="status === 'cancelled'" class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="m6 6 12 12M18 6 6 18"></path>
            </svg>
            <svg *ngIf="status !== 'paid' && status !== 'cancelled'" class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v4m0 4h.01M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0Z"></path>
            </svg>
          </div>

          <span class="mb-3 rounded-full px-3 py-1 text-xs font-semibold capitalize"
            [ngClass]="getStatusClass()">{{ getStatusLabel() }}</span>
          <h1 class="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            {{ getStatusTitle() }}
          </h1>
          <p class="mt-3 max-w-md text-sm leading-6 text-slate-600 sm:text-base" role="status" aria-live="polite">{{ message }}</p>
          <p *ngIf="newsletterError" class="mt-3 text-sm text-amber-800" role="status">
            {{ newsletterError }}
            <button type="button" (click)="retryNewsletterSubscription()"
              class="ml-1 font-semibold underline">Try again</button>
          </p>
        </div>

        <section *ngIf="paymentId || orderReference"
          class="mt-7 rounded-xl border border-slate-200 bg-slate-50 px-4 sm:px-5"
          aria-label="Payment details">
          <div *ngIf="paymentId"
            class="flex flex-col gap-1 border-b border-slate-200 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <span class="text-sm text-slate-500">Payment reference</span>
            <span class="break-all font-mono text-sm font-medium text-slate-800 sm:text-right">{{ paymentId }}</span>
          </div>
          <div *ngIf="orderReference" class="flex items-center justify-between gap-4 border-b border-slate-200 py-3">
            <span class="text-sm text-slate-500">Order number</span>
            <span class="font-medium text-slate-900">{{ orderReference }}</span>
          </div>
          <div class="flex items-center justify-between gap-4 py-3">
            <span class="text-sm text-slate-500">Date</span>
            <span class="text-sm font-medium text-slate-800">{{ orderDate }}</span>
          </div>
        </section>

        <div *ngIf="status === 'confirmed' && !orderReference"
          class="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
          Keep your payment reference and contact our team so we can match the confirmed payment to your order.
          <a [href]="supportEmailLink"
            class="mt-2 inline-flex font-semibold text-amber-900 underline underline-offset-2">
            Contact support
          </a>
        </div>

        <div class="mt-7 flex flex-col gap-3 sm:flex-row">
          <button (click)="continueShopping()" [disabled]="isFinalizing"
            class="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg bg-orange-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2">
            {{ isFinalizing
              ? 'Checking payment status...'
              : canRetryPaymentStatus
              ? 'Check payment status again'
              : status === 'paid' || status === 'confirmed'
                ? 'Continue shopping'
                : 'Return to checkout and retry' }}
          </button>
          <a *ngIf="status === 'confirmed' && !orderReference" [href]="supportEmailLink"
            class="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2">
            Email support
          </a>
        </div>
        <a *ngIf="isGuestOrder && orderReference" routerLink="/auth/guest-order"
          class="mt-5 inline-block text-sm font-semibold text-orange-700 underline underline-offset-2 hover:text-orange-800">
          Track this guest order
        </a>
      </main>
    </div>
  `,
  styleUrls: [],
})
export class CheckoutSuccessComponent implements OnInit, OnDestroy {
  paymentId = '';
  orderReference = '';
  isGuestOrder = false;
  status = 'processing';
  orderDate = '';
  message = 'Verifying your payment and preparing your order...';
  newsletterError = '';
  isFinalizing = false;
  private newsletterRequestStarted = false;
  private paymentCheckTimer?: ReturnType<typeof setTimeout>;

  get supportEmailLink(): string {
    const subject = encodeURIComponent(`Payment confirmation - ${this.paymentId}`);
    const body = encodeURIComponent(
      `My payment was confirmed but I could not complete checkout.\nPayment ID: ${this.paymentId}`,
    );
    return `mailto:sklep@brocomindustry.com?subject=${subject}&body=${body}`;
  }

  get canRetryPaymentStatus(): boolean {
    return !!this.paymentId &&
      ['error', 'new', 'pending', 'processing', 'confirmed', 'unknown'].includes(this.status);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cartService: CartService,
    private paynowService: PaynowService,
    private newsletterService: NewsletterService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.paymentId = params['paymentId'] || '';
      this.orderDate = new Date().toLocaleDateString();

      if (!this.paymentId) {
        this.status = 'error';
        this.message = 'We could not verify a payment ID. Your order has not been created.';
        return;
      }

      this.finalizePayment();
    });
  }

  ngOnDestroy(): void {
    if (this.paymentCheckTimer) {
      clearTimeout(this.paymentCheckTimer);
    }
  }

  private markPaymentCancelled(): void {
    this.status = 'cancelled';
    this.message = 'The payment was cancelled. No order was created; you can return to checkout and try again.';
    if (this.paymentId) {
      sessionStorage.removeItem(`brocom_pending_checkout_${this.paymentId}`);
      sessionStorage.removeItem(`brocom_pending_newsletter_${this.paymentId}`);
    }
    this.paynowService.clearCheckoutKey();
  }

  private isCancelledStatus(status: string): boolean {
    return ['cancelled', 'canceled', 'abandoned'].includes(status);
  }

  private finalizePayment(attempt = 0): void {
    if (this.isFinalizing) return;
    if (this.paymentCheckTimer) {
      clearTimeout(this.paymentCheckTimer);
      this.paymentCheckTimer = undefined;
    }
    const storageKey = `brocom_pending_checkout_${this.paymentId}`;
    this.isFinalizing = true;
    this.paynowService
      .finalizeOrderPayment(this.paymentId)
      .subscribe({
        next: (result) => {
          this.isFinalizing = false;
          this.status = (result.status || 'unknown').toLowerCase();
          if (result.orderCreated) {
            this.status = 'paid';
            this.orderReference = result.orderReference || '';
            this.isGuestOrder = result.isGuestOrder === true;
            this.message = 'Thank you for your order. Your payment has been processed successfully.';
            this.cartService.clearCart();
            sessionStorage.removeItem(storageKey);
            this.clearCheckoutDraft();
            this.paynowService.clearCheckoutKey();
            this.subscribeToNewsletterIfRequested();
          } else {
            if (this.isCancelledStatus(this.status)) {
              this.markPaymentCancelled();
              return;
            }

            if (['new', 'pending', 'processing'].includes(this.status) && attempt < 5) {
              this.message = 'Payment is still being confirmed. Checking again...';
              this.paymentCheckTimer = setTimeout(() => {
                this.paymentCheckTimer = undefined;
                this.finalizePayment(attempt + 1);
              }, 2000);
              return;
            }
            if (['new', 'pending', 'processing'].includes(this.status)) {
              this.message =
                'Your payment is still being confirmed. Do not start another payment; check the status again shortly.';
              return;
            }
            if (this.status === 'confirmed') {
              this.message =
                'Your payment is confirmed, but order creation is still being completed. Do not pay again. Please check again or contact support with your payment reference.';
              return;
            }
            this.message = 'The payment was not completed, so no order was created. You can return to checkout and try again.';
            if (['rejected', 'error', 'expired', 'cancelled', 'canceled', 'declined', 'failed', 'abandoned'].includes(this.status)) {
              sessionStorage.removeItem(storageKey);
              sessionStorage.removeItem(
                `brocom_pending_newsletter_${this.paymentId}`,
              );
            }
          }
        },
        error: () => {
          this.isFinalizing = false;
          this.status = 'error';
          this.message = 'We could not verify the payment status yet. Do not start another payment while this one is being checked. Retry the status check or contact support with your payment ID.';
        },
      });
  }

  private clearCheckoutDraft(): void {
    try {
      const storedUser =
        localStorage.getItem('clientUser') ||
        sessionStorage.getItem('clientUser');
      const user = storedUser ? JSON.parse(storedUser) : null;
      sessionStorage.removeItem(getCheckoutDraftStorageKey(user));
      sessionStorage.removeItem('brocom_checkout_draft');
    } catch (error) {
      console.warn('Could not clear the completed checkout draft:', error);
    }
  }

  private subscribeToNewsletterIfRequested(): void {
    if (this.newsletterRequestStarted || !this.paymentId) return;
    const storageKey = `brocom_pending_newsletter_${this.paymentId}`;
    const email = sessionStorage.getItem(storageKey)?.trim();
    if (!email) return;

    this.newsletterRequestStarted = true;
    this.newsletterService.subscribe(email, true).subscribe({
      next: () => {
        sessionStorage.removeItem(storageKey);
        this.newsletterError = '';
      },
      error: () => {
        this.newsletterRequestStarted = false;
        this.newsletterError =
          'Your order is confirmed, but we could not complete your newsletter signup.';
      },
    });
  }

  retryNewsletterSubscription(): void {
    this.newsletterError = '';
    this.subscribeToNewsletterIfRequested();
  }

  continueShopping(): void {
    if (this.canRetryPaymentStatus) {
      this.status = 'processing';
      this.message = 'Checking your payment status...';
      this.finalizePayment();
      return;
    }
    this.router.navigate([this.status === 'paid' || this.status === 'confirmed' ? '/' : '/checkout']);
  }

  getStatusTitle(): string {
    if (this.status === 'paid') return 'Payment successful';
    if (this.status === 'cancelled') return 'Payment cancelled';
    if (this.status === 'confirmed') return 'Payment confirmed';
    if (['processing', 'pending', 'new'].includes(this.status)) return 'Payment being verified';
    return 'Payment status';
  }

  getStatusLabel(): string {
    if (this.status === 'paid') return 'Paid';
    if (this.status === 'cancelled') return 'Cancelled';
    return this.status;
  }

  getStatusIconClass(): string {
    if (this.status === 'paid') return 'bg-emerald-100 text-emerald-700';
    if (this.status === 'cancelled') return 'bg-slate-100 text-slate-600';
    if (this.status === 'confirmed') return 'bg-amber-100 text-amber-700';
    if (['error', 'rejected', 'failed'].includes(this.status)) return 'bg-red-100 text-red-700';
    return 'bg-blue-100 text-blue-700';
  }

  getStatusClass(): string {
    switch (this.status.toLowerCase()) {
      case 'paid':
      case 'successful':
      case 'success':
      case 'completed':
        return 'bg-emerald-100 text-emerald-800';
      case 'pending':
      case 'processing':
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-amber-100 text-amber-800';
      case 'failed':
      case 'error':
      case 'rejected':
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
      case 'canceled':
        return 'bg-slate-100 text-slate-700';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
}
